import { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { IconX } from '@tabler/icons-react';
import { AnimatePresence, motion } from 'framer-motion';
import Image from './Image';
import useImageViewerStore from '../../store/ImageViewerStore';

function ImageViewer() {
  const { isViewerOpen, currentImageUrl, currentImageAlt, setIsViewerOpen } = useImageViewerStore();

  const handleClose = useCallback(() => setIsViewerOpen(false), [setIsViewerOpen]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && handleClose();
    if (isViewerOpen) window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isViewerOpen, handleClose]);

  const ext = currentImageUrl.split('.').pop()?.split('?')[0]?.toLowerCase();
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || '');
  const isVideo = ['mp4', 'webm'].includes(ext || '');

  return createPortal(
    <AnimatePresence initial={false}>
      {isViewerOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.1 }}
          className="fixed top-0 bottom-0 left-0 right-0 flex items-center justify-center w-full h-full bg-black/80 z-[999999] backdrop-blur-sm"
          onClick={handleClose}
        >
          <div className="relative flex items-center justify-center w-full h-full p-5 select-none">
            <button type="button" onClick={handleClose} className="absolute right-8 top-8">
              <IconX size={35} className="text-white" />
            </button>
            {isImage && (
              <motion.div
                initial={{ scale: 0.9, filter: 'blur(10px)' }}
                animate={{ scale: 1, filter: 'blur(0px)' }}
                exit={{ scale: 0.9, filter: 'blur(10px)' }}
                transition={{ duration: 0.2 }}
              >
                <Image
                  src={currentImageUrl}
                  alt={currentImageAlt}
                  className="max-h-[80vh] md:max-h-[85vh] w-auto h-auto object-contain rounded-lg shadow-2xl cursor-zoom-out"
                />
              </motion.div>
            )}
            {isVideo && (
              <video controls width={700}>
                <source src={currentImageUrl} type={`video/${ext}`} />
                <track kind="captions" srcLang="en" label="English captions" />
                Your browser does not support the video tag.
              </video>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

export default ImageViewer;
